/** @format */

import {FeatureId} from '@src/contexts'
import {PluginGroup, PluginKind} from '@src/types/Plugin'

export type PluginConfig = {
  // A sort of id -- e.g. 'slack', 'notion'
  kind: PluginKind
  // Human-readable name
  name: string
  // Human-readable description
  description: string
  // The plan feature that unlocks this plugin, and what disables it in the form
  // editor. Absent means every plan has it.
  requires?: FeatureId
  // The plan feature to name when offering an upgrade, where that differs from
  // what gates the editor. Only Stripe: you can set it up and run test mode on
  // any plan, so `requires` stays unset, but it is sold on Professional. Falls
  // back to `requires`.
  upgradeRequires?: FeatureId
  // Display setting only used by form editor so far
  group: PluginGroup
}

// The plugin catalogue, shared by:
// - the form editor (usePlugins)
// - the plans page comparison table
//
// The idea is: this is a list of the plugins and their /settings/, like which
// plan/feature you need in order to use this plugin.
// Analogous to how features are articulated in plans.py
//
// /Display grouping and ordering/ can be decided separately by each consumer.
// Today the plans page does that with PLUGIN_TABLE_SECTIONS
// But the AddActionModal does it here, with the 'group' setting--should maybe
// move that out of here.
// And Plugins/index.tsx just reorders them alphabetically.
export const PLUGIN_CONFIGS: PluginConfig[] = [
  {
    kind: 'email',
    group: 'Notifications',
    name: 'Email',
    description: 'Send an email for each form submission',
  },
  {
    kind: 'discord',
    group: 'Notifications',
    name: 'Discord',
    description: 'Send a Discord message to your channel',
  },
  {
    kind: 'slack',
    group: 'Notifications',
    name: 'Slack',
    description: 'Send a Slack message to your channel',
  },
  {
    kind: 'telegram',
    group: 'Notifications',
    name: 'Telegram',
    description: 'Send a Telegram message when a submission is received',
  },
  {
    kind: 'airtable',
    group: 'Productivity',
    name: 'Airtable',
    description: 'Add a row to your Airtable table',
    requires: 'plugins',
  },
  {
    kind: 'github',
    group: 'Productivity',
    name: 'GitHub',
    description: 'Create a GitHub issue for each form submission',
    requires: 'plugins',
  },
  {
    kind: 'google-sheets',
    group: 'Productivity',
    name: 'Google Sheets',
    description: 'Send form submissions to a Google Sheets spreadsheet',
    requires: 'plugins',
  },
  {
    kind: 'notion',
    group: 'Productivity',
    name: 'Notion',
    description: 'Add submissions to a database in Notion',
    requires: 'plugins',
  },
  {
    kind: 'trello',
    group: 'Productivity',
    name: 'Trello',
    description: 'Create a card on your Trello board for each form submission',
    requires: 'plugins',
  },
  {
    kind: 'hubspot',
    group: 'CRM Systems',
    name: 'HubSpot',
    description:
      'Create a new contact in HubSpot when a submission is received',
    requires: 'premium_plugins',
  },
  {
    kind: 'activecampaign',
    group: 'CRM Systems',
    name: 'ActiveCampaign',
    description:
      'Create a new contact in ActiveCampaign when a submission is received',
    requires: 'premium_plugins',
  },
  {
    kind: 'pipedrive',
    group: 'CRM Systems',
    name: 'Pipedrive',
    description: 'Add a lead to your Pipedrive inbox',
    requires: 'premium_plugins',
  },
  {
    kind: 'salesforce',
    group: 'CRM Systems',
    name: 'Salesforce',
    description: 'Add a lead to your Salesforce Leads',
    requires: 'premium_plugins',
  },
  {
    kind: 'mailchimp',
    group: 'Newsletters',
    name: 'Mailchimp',
    description: 'Add a new subscriber to a Mailchimp list',
    requires: 'premium_plugins',
  },
  {
    kind: 'mailerlite',
    group: 'Newsletters',
    name: 'MailerLite',
    description: 'Add a new subscriber to your MailerLite account',
    requires: 'premium_plugins',
  },
  {
    kind: 'convertkit',
    group: 'Newsletters',
    name: 'ConvertKit',
    description: 'Add a new subscriber to your ConvertKit account',
    requires: 'premium_plugins',
  },
  {
    kind: 'klaviyo',
    group: 'Newsletters',
    name: 'Klaviyo',
    description: 'Add a new profile to your Klaviyo list',
    requires: 'premium_plugins',
  },
  {
    kind: 'constantcontact',
    group: 'Newsletters',
    name: 'Constant Contact',
    description: 'Trigger a constant-contact when a submission is received',
    requires: 'premium_plugins',
  },
  {
    kind: 'linear',
    group: 'Productivity',
    name: 'Linear',
    description: 'Create a Linear issue from a form submission',
    requires: 'plugins',
  },
  {
    kind: 'asana',
    group: 'Productivity',
    name: 'Asana',
    description: 'Trigger Asana to create a task when a submission is received',
    requires: 'plugins',
  },
  {
    kind: 'freshdesk',
    group: 'Support',
    name: 'Freshdesk',
    description: 'Add a ticket to your Freshdesk support queue',
    requires: 'premium_plugins',
  },
  {
    kind: 'gladly',
    group: 'Support',
    name: 'Gladly',
    description: 'Send form submissions to your Gladly inbox',
    requires: 'premium_plugins',
  },
  {
    kind: 'gorgias',
    group: 'Support',
    name: 'Gorgias',
    description: 'Create a Gorgias support ticket for each form submission',
    requires: 'premium_plugins',
  },
  {
    kind: 'zendesk',
    group: 'Support',
    name: 'Zendesk',
    description: 'Add a ticket to your Zendesk support queue',
    requires: 'premium_plugins',
  },
  {
    kind: 'stripe_v2',
    group: 'Other',
    name: 'Stripe',
    description: 'Accept payments through Stripe',
    // Sold on Professional and up, which is what the plans page's row says, but
    // deliberately not `requires`: you can set Stripe up and run it in test
    // mode on any plan, and only live mode is gated.
    upgradeRequires: 'premium_plugins',
  },
  {
    kind: 'zapier',
    group: 'Other',
    name: 'Zapier',
    description: 'Trigger Zapier actions when a submission is received',
    requires: 'premium_plugins',
  },
  {
    kind: 'autoresponse',
    group: 'Other',
    name: 'Auto Response',
    description:
      'Send a confirmation email to the submitter that their submission has been received',
    requires: 'autoresponses',
  },
  {
    kind: 'webhook',
    group: 'Other',
    name: 'Webhook',
    description: 'Trigger a webhook when a submission is received',
    requires: 'premium_plugins',
  },
]

// Redshift is deliberately not in the above list: it is gated on a per-account
// custom feature (account.features.redshift) rather than on a plan, so
// usePlugins splices it in and the plans page leaves it out.
export const REDSHIFT_PLUGIN_CONFIG: PluginConfig = {
  kind: 'redshift',
  group: 'Productivity',
  name: 'Redshift',
  description: 'Add rows in Redshift table',
  requires: 'premium_plugins',
}

// The feature a plugin is gated on, or undefined when every plan has it. Lives
// next to the catalogue rather than in the caller, since an upgrade link is
// handed only the plugin kind.
//
// Redshift counts even though it sits outside PLUGIN_CONFIGS: being outside is
// about where it gets listed, not what it needs. Its custom feature and
// premium_plugins are granted separately, and the backend enforces both
// (plugin_update answers 402, the dispatcher refuses to send), so an account can
// hold the grant on a plan that cannot use it. Its upgrade modal is what says so.
export function pluginRequires(kind: PluginKind): FeatureId | undefined {
  const config = [...PLUGIN_CONFIGS, REDSHIFT_PLUGIN_CONFIG].find(
    config => config.kind === kind,
  )
  return config?.upgradeRequires ?? config?.requires
}

// Roughly most-used first. Kinds absent from this list sort after everything
// in it (see pluginConfigsByPopularity), so the catalogue stays the source of
// truth for *which* plugins exist — this only says what order to show them in.
//
// PLUGIN_CONFIGS itself keeps its own grouped order, which the form editor and
// the plans page both read meaning into.
const POPULARITY_ORDER: PluginKind[] = [
  'email',
  'google-sheets',
  'telegram',
  'webhook',
  'slack',
  'autoresponse',
  'discord',
  'zapier',
  'notion',
  'airtable',
  'hubspot',
  'mailchimp',
  'stripe_v2',
  'mailerlite',
  'gorgias',
  'pipedrive',
  'salesforce',
  'klaviyo',
  'trello',
  'github',
  'convertkit',
  'zendesk',
  'asana',
  'activecampaign',
  'constantcontact',
  'linear',
  'freshdesk',
]

/**
 * PLUGIN_CONFIGS ordered by POPULARITY_ORDER, for places that want to lead with
 * the plugins people actually use — the register page's logo row, so far.
 *
 * Anything missing from POPULARITY_ORDER sorts after everything in it, keeping
 * its catalogue order (Array.sort is stable). So a plugin added to the
 * catalogue still shows up here without being ranked, just at the back.
 */
export function pluginConfigsByPopularity(): PluginConfig[] {
  const rank = new Map(POPULARITY_ORDER.map((kind, i) => [kind, i]))
  const unranked = POPULARITY_ORDER.length
  return [...PLUGIN_CONFIGS].sort(
    (a, b) => (rank.get(a.kind) ?? unranked) - (rank.get(b.kind) ?? unranked),
  )
}
